/* Reading Room — the theme's boot shim. dark-conversion, 2026-09-23.
 *
 * The engine exposes window.DF.ReadingRoom.mount(rootEl, opts) and nothing else: the contract
 * (§2.1) makes finding and mounting the elements the theme's job, not the engine's. This file is
 * that job, and only that job. It is loaded after the engine, deferred, so it runs after first
 * paint (R-V1).
 *
 * It is written to work with either engine — dark-dev's assets/df-reading-room.js or this
 * department's stand-in — because both hold the same mount contract. The stand-in boots itself,
 * so mounting twice is guarded by the engine's own idempotence and by the marker below.
 */
(function () {
  'use strict';
  var SEL = 'df-table, df-yes-no, df-daily-card, df-birth-cards, df-deck-offer, df-ai-note';

  function ready(node) {
    /* the contract's signal is a df-ready CustomEvent on the element; the attribute is this
       theme's own convenience so a walk, a screenshot or a CSS rule can wait for it too. */
    node.setAttribute('data-df-ready', '1');
  }

  /* The question box lives in a CLOSED shadow root (contract §2.2: rendered by the THEME, not the
     engine). Measured on the preview 2026-09-23: with the question typed into an ordinary <input>,
     Meta's app pixel POSTed the typed text to facebook.com. The pixel is a Shopify app pixel, not
     theme code, so it cannot be gated from the theme — but its scraper walks the light DOM, and a
     closed shadow root is not in it. R-X2 says the question never leaves her phone.

     2026-09-24 BUG FOUND (hub, third phone walk): `<df-question>` was never a registered custom
     element in dev's engine (it only ever existed in this department's retired stand-in) — the
     engine reads `opts.questionEl.value`, and an unmounted `<df-question>` has no `.value` at all,
     so every typed question was silently discarded with no console error. This mounts the real
     input the SAME way the retired stand-in did, and hands the engine that real element instead of
     the empty wrapper. */
  function mountQuestion(host) {
    if (!host || host.__rrQ) return host && host.__rrQ;
    var root;
    try { root = host.attachShadow({ mode: 'closed', delegatesFocus: true }); } catch (e) { return null; }
    var st = document.createElement('style');
    st.textContent = ':host{display:block;margin:0 0 1rem}' +
      'label{display:block;font:600 .88rem/1.4 var(--rr-sans,system-ui);color:#2C1A0E;margin:0 0 .35rem}' +
      'input{width:100%;min-height:48px;padding:.6rem .75rem;font:400 1rem/1.4 var(--rr-sans,system-ui);' +
      'color:#2C1A0E;background:#fff;border:1px solid #8B6F5A;border-radius:8px;box-sizing:border-box}' +
      'input:focus-visible{outline:3px solid #2C1A0E;outline-offset:2px}' +
      'p{font:400 .82rem/1.4 var(--rr-sans,system-ui);color:#8B6F5A;margin:.35rem 0 0}';
    root.appendChild(st);
    var id = 'q-' + Math.random().toString(36).slice(2, 8);
    var lab = document.createElement('label');
    lab.setAttribute('for', id);
    lab.textContent = host.getAttribute('label') || '';
    var inp = document.createElement('input');
    inp.type = 'text';
    inp.id = id;
    inp.maxLength = 200;
    inp.autocomplete = 'off';
    inp.spellcheck = false;
    inp.setAttribute('autocapitalize', 'sentences');
    inp.placeholder = host.getAttribute('placeholder') || '';
    inp.setAttribute('data-hj-suppress', '');
    inp.setAttribute('data-clarity-mask', 'true');
    inp.setAttribute('data-recording-sensitive', '');
    var hint = document.createElement('p');
    hint.textContent = host.getAttribute('hint') || '';
    root.appendChild(lab); root.appendChild(inp); root.appendChild(hint);
    host.setAttribute('tabindex', '-1');
    host.setAttribute('data-rr-q', '1');
    host.__rrQ = inp;
    return inp;
  }
  function resolveQuestionEl(sel) {
    var node = sel ? document.querySelector(sel) : null;
    if (!node) return null;
    if (node.tagName && node.tagName.toLowerCase() === 'df-question') return mountQuestion(node);
    return node;
  }

  function boot() {
    var DF = window.DF && window.DF.ReadingRoom;
    if (!DF || typeof DF.mount !== 'function') return false;
    var nodes = document.querySelectorAll(SEL);
    if (!nodes.length) return true;
    Array.prototype.forEach.call(nodes, function (node) {
      if (node.getAttribute('data-df-mounted')) return;
      node.setAttribute('data-df-mounted', '1');
      node.addEventListener('df-ready', function () { ready(node); });
      var opts = {};
      var qsel = node.getAttribute('question-el');
      if (qsel) {
        var qEl = resolveQuestionEl(qsel);
        if (qEl) opts.questionEl = qEl;
      }
      try {
        var inst = DF.mount(node, opts);
        if (inst) ready(node);
      } catch (e) {
        /* R-F0: leave the server-rendered content in place rather than a broken tool */
        node.removeAttribute('data-df-mounted');
        if (window.console && console.warn) console.warn('[rr] mount failed', node.tagName, e && e.message);
      }
    });
    return true;
  }

  /* the engine may still be parsing when this runs; try, then watch briefly for it to appear */
  if (!boot()) {
    var tries = 0;
    var t = setInterval(function () {
      if (boot() || ++tries > 60) clearInterval(t);
    }, 100);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
})();
